import React, { useCallback, useState, useEffect, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import useApi from "../Api/Api";
import Button from "../Components/Button";
import EditArtist from "../Forms/EditArtist";
import { UserContext } from "../Auth/UserContextProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faTrash } from "@fortawesome/free-solid-svg-icons";
import ArtistsLayout from "./ArtistsLayout";

const ArtistDetails = () => {
  const params = useParams();
  const api = useApi();
  const [showEditForm, setShowEditForm] = useState(false);
  const { isAdmin } = useContext(UserContext);
  const [artistLastUpdatedBy, setArtistLastUpdatedBy] = useState({});
  const [tempManagerPercentage, setTempManagerPercentage] = useState(0);
  const [artist, setArtist] = useState({
    name: "",
    payment_info: "",
  });

  const [artistSplits, setArtistSplits] = useState([]);

  const handleShowEditForm = () => {
    setShowEditForm(!showEditForm);
  };

  const getArtist = useCallback(() => {
    api.get(`/artists/${params.artistId}`).then((r) => {
      setArtist(r.data);
      setTempManagerPercentage(
        parseFloat(r.data?.manager_commission_percentage * 100)
      );
    });
  }, [api, params.artistId]);

  const getArtistSplits = useCallback(() => {
    api.get(`/artist_splits?artist_id=${params.artistId}`).then((r) => {
      setArtistSplits(r.data);
    });
  }, [params.artistId, api]);

  useEffect(() => {
    getArtist();
    getArtistSplits();
  }, [getArtist, getArtistSplits]);

  useEffect(() => {
    artist.versions?.length &&
      api
        .get(`/users/${artist.versions[artist.versions?.length - 1].whodunnit}`)
        .then((r) => {
          setArtistLastUpdatedBy(r.data);
        });
  }, [api, artist.versions]);

  const deleteSplit = (e, splitToDelete) => {
    e.preventDefault();
    const check = window.confirm(
      `Are you sure you want to delete artist split from ${splitToDelete.effective_date}?`
    );
    if (check) {
      api.delete(`artist_splits/${splitToDelete.id}`).then(() => {
        getArtistSplits();
      });
    }
  };

  return (
    <ArtistsLayout>
      <div className="text-black bg-gray-100 pt-2 pb-8">
        {showEditForm && (
          <EditArtist
            handleShowEditForm={handleShowEditForm}
            getArtist={getArtist}
            getArtistSplits={getArtistSplits}
            artist={artist}
            setArtist={setArtist}
            setArtistSplits={setArtistSplits}
            tempManagerPercentage={tempManagerPercentage}
            setTempManagerPercentage={setTempManagerPercentage}
          ></EditArtist>
        )}
        <div className={showEditForm ? "blur" : ""}>
          <div className="my-8 mx-8 py-9 px-12 text-black bg-white rounded-lg border">
            <Link to="/artists">
              <div className="mt-1 font-medium text-black text-sm">
                <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to
                artists list
              </div>
            </Link>
            <span className="flex justify-end text-sm -mt-5 -mb-7">
              <span className="text-white h-8 mb-1">-</span>
              {isAdmin && !showEditForm && (
                <Button
                  onClick={handleShowEditForm}
                  className="button"
                  text="Edit"
                ></Button>
              )}
            </span>
            <div className="pt-3 pb-7 text-2xl font-medium">{artist.name}</div>
            <div className="text-black font-light text-sm">
              <div>
                <div className="text-lg font-medium border-b">
                  <div className="pt-12 pb-5">Artist Details</div>
                </div>
                <div className="flex pt-8 pb-7 border-b">
                  <div className="w-1/2">Name</div>
                  <div className="w-1/5 font-semibold">{artist.name}</div>
                </div>
                {artistSplits.length > 0 && isAdmin && (
                  <>
                    <div className="text-lg font-medium border-b">
                      <div className="pt-12 pb-5">Artist Split</div>
                    </div>
                    <div className="flex pt-8 pb-7 border-b">
                      <div className="w-1/2">Percentage</div>
                      <div className="w-1/5 font-semibold">
                        {parseFloat(
                          artistSplits[0]?.percentage
                        )?.toLocaleString("en-US", {
                          style: "percent",
                        })}
                      </div>
                    </div>
                    <div className="flex pt-8 pb-7 border-b">
                      <div className="w-1/2">Effective Date</div>
                      <div className="w-1/5 font-semibold">
                        {artistSplits[0]?.effective_date?.slice(5, 7)}/
                        {artistSplits[0]?.effective_date?.slice(8, 10)}/
                        {artistSplits[0]?.effective_date?.slice(0, 4)}
                      </div>
                    </div>
                  </>
                )}
                <div className="py-6 text-lg font-medium border-b">
                  <div className="mt-5">Payment Details</div>
                </div>
                {isAdmin && (
                  <>
                    <div className="flex py-7 border-b">
                      <div className="w-1/2">Road manager daily rate when artist vends</div>
                      <div className="w-1/5 font-semibold">
                        {artist.manager_payment}
                      </div>
                    </div>
                    <div className="flex py-7 border-b">
                      <div className="w-1/2">Road manager daily rate when artist doesn't vend</div>
                      <div className="w-1/5 font-semibold">
                        {artist.venue_vended_manager_payment}
                      </div>
                    </div>
                    <div className="flex py-7 border-b">
                      <div className="w-1/2">
                        Road manager commission percentage
                      </div>
                      <div className="w-1/5 font-semibold">
                        {parseFloat(
                          artist.manager_commission_percentage
                        )?.toLocaleString("en-US", {
                          style: "percent",
                        })}
                      </div>
                    </div>
                  </>
                )}
                <div className="flex py-7 border-b">
                  <div className="w-1/2">Business Name</div>
                  <div className="w-1/5 font-semibold">
                    {artist.business_name}
                  </div>
                </div>
                <div className="flex py-7 border-b">
                  <div className="w-1/2">EIN / Tax ID</div>
                  <div className="w-1/5 font-semibold">{artist.tax_id}</div>
                </div>
                <div className="flex py-7 border-b">
                  <div className="w-1/2">Street Address</div>
                  <div className="w-1/5 font-semibold">
                    {artist.street_address}
                  </div>
                </div>
                <div className="flex py-7 border-b">
                  <div className="w-1/2">City</div>
                  <div className="w-1/5 font-semibold">{artist.city}</div>
                </div>
                <div className="flex py-7 border-b">
                  <div className="w-1/2">State / Province</div>
                  <div className="w-1/5 font-semibold">{artist.state}</div>
                </div>
                <div className="flex py-7 border-b">
                  <div className="w-1/2">Zip / Postal Code</div>
                  <div className="w-1/5 font-semibold">{artist.zip_code}</div>
                </div>
                {isAdmin && (
                  <>
                    <div className="pt-6 text-lg font-medium">
                      <div className="mt-5 mb-7 pb-6 border-b">
                        Split History
                      </div>
                      <div className="flex font-light mb-2 text-base font-normal">
                        <div className="w-1/2">Effective Date</div>
                        <div className="w-1/5">Percentage</div>
                      </div>
                    </div>
                  </>
                )}
                {artistSplits.length > 0 &&
                  isAdmin &&
                  artistSplits.map((split) => {
                    return (
                      <div key={split.id} className="flex py-1">
                        <div className="w-1/2">
                          {split.effective_date.split("T")[0]}
                        </div>
                        <div className="w-1/5 font-semibold">
                          {parseFloat(split.percentage)?.toLocaleString(
                            "en-US",
                            {
                              style: "percent",
                            }
                          )}
                        </div>
                        {isAdmin && (
                          <button
                            className="w-1/5"
                            onClick={(e) => deleteSplit(e, split)}
                          >
                            <FontAwesomeIcon icon={faTrash}></FontAwesomeIcon>
                          </button>
                        )}
                      </div>
                    );
                  })}
                <div className="text-lg font-medium border-b">
                  <div className="pt-12 pb-5">Artist Last Modified By</div>
                </div>
                <div className="flex pt-8 pb-7 border-b">
                  <div className="w-1/2">Name</div>
                  <div className="w-1/5 font-semibold">
                    {artistLastUpdatedBy.first_name}{" "}
                    {artistLastUpdatedBy.last_name}
                  </div>
                </div>
                <div className="flex pt-8 pb-7 border-b">
                  <div className="w-1/2">Email</div>
                  <div className="w-1/5 font-semibold">
                    {artistLastUpdatedBy.email}
                  </div>
                </div>
                <div className="flex pt-8 pb-7 border-b">
                  <div className="w-1/2">Date</div>
                  {artist.versions?.length > 0 && (
                    <div className="w-1/5 font-semibold">
                      {
                        artist.versions[
                          artist.versions?.length - 1
                        ].created_at?.split("T")[0]
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ArtistsLayout>
  );
};

export default ArtistDetails;
